/**
 * **Where a phone-map card opens** (mobile web; user decision 2026-11-03, with iOS/Android `ActivityMapScreen`): next to
 * what was pressed, not centred on the screen. Pure: the map hands the card the trigger's on-screen rect (its
 * `getBoundingClientRect()`, so it already carries the page's scroll and the board's pinch zoom), the card measures
 * itself, and this says where its top-left goes. Every number is in viewport pixels.
 *
 * - An agent's dot or the Maestro: BESIDE it. The card's left is the trigger's centre + `besideGap`; when that runs past
 *   the right gutter it flips to the other side (its right at the centre − `besideGap`). Its top is the centre −
 *   `besideRise`. Then it is clamped inside the side gutters and the band.
 * - A tray chip, a tray's "+N" or the tools tray's "+": ABOVE it — the card's bottom `aboveGap` over the trigger's
 *   top, centred on it, clamped inside the side gutters — or BELOW it (its top `aboveGap` under the trigger's
 *   bottom) when above would cross the band's top inset. Below, it is clamped into the band too (as Android's
 *   `popoverOrigin` is), so a tall card — a long "+N" list — never runs past the band's bottom inset; when it is
 *   taller than the band allows, its top stays on the band's top inset.
 */

/** How a card stands to its trigger. */
export type PhoneCardAnchorMode = 'beside' | 'above';

/** A box in viewport pixels — a `DOMRect`'s edges. */
export interface PopoverRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** The gap from a dot's centre to the card beside it. */
export const besideGap = 21;
/** How far above a dot's centre the card beside it starts. */
export const besideRise = 55;
/** The gap between a chip and the card above (or below) it. */
export const aboveGap = 19;
/** The screen's side gutters a card never crosses. */
export const cardGutter = 20;
/** How far inside the band's top and bottom a card stays. */
export const bandInset = 8;

export function phoneCardPlacement({
  trigger,
  mode,
  card,
  screenWidth,
  band,
}: {
  trigger: PopoverRect;
  mode: PhoneCardAnchorMode;
  /** The card's own measured size. */
  card: { width: number; height: number };
  screenWidth: number;
  /** The band's top and bottom on screen (the part of it in the viewport). */
  band: { top: number; bottom: number };
}): { left: number; top: number } {
  const cx = (trigger.left + trigger.right) / 2;
  const cy = (trigger.top + trigger.bottom) / 2;
  const minLeft = cardGutter;
  const maxLeft = Math.max(minLeft, screenWidth - cardGutter - card.width);
  const clampLeft = (left: number) => Math.min(maxLeft, Math.max(minLeft, left));
  const minTop = band.top + bandInset;
  const maxTop = Math.max(minTop, band.bottom - bandInset - card.height);
  const clampTop = (top: number) => Math.min(maxTop, Math.max(minTop, top));

  if (mode === 'beside') {
    let left = cx + besideGap;
    if (left + card.width > screenWidth - cardGutter) left = cx - besideGap - card.width;
    return { left: clampLeft(left), top: clampTop(cy - besideRise) };
  }

  const left = clampLeft(cx - card.width / 2);
  const above = trigger.top - aboveGap - card.height;
  return { left, top: above < minTop ? clampTop(trigger.bottom + aboveGap) : above };
}
